import { KeyboardEvent, RefObject, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { TermsNoticeCandidate, TermsNoticeSendSelectedResult, TermsNoticeSkipped } from '../../types';
import TypedConfirmDialog from '../TypedConfirmDialog';
import { useDialogFocus } from '../config/ConfigHistoryDialog';
import TermsStatusBadge from './TermsStatusBadge';
import { RECIPIENT_KIND_LABEL, isSignedStatus, plural, skipReasonLabel, termsNoticeErrorMessage } from './termsNotice';

interface TermsNoticeSendDialogProps {
  onClose: () => void;
  /** Called once after at least one request went through, so the garage list can refresh. */
  onSent: () => void;
}

type Step = 'pick' | 'confirm' | 'sending' | 'result';
type Filter = '' | 'SIGNED' | 'NOT_SIGNED';

export const SEND_CONFIRM_PHRASE = 'SEND';
/** The backend takes at most 100 ids per request (ALB idle timeout); larger picks go in sequential chunks. */
export const TERMS_NOTICE_SEND_CHUNK = 100;

const FILTER_OPTIONS: { value: Filter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'SIGNED', label: 'Signed' },
  { value: 'NOT_SIGNED', label: 'Not signed' },
];

const DISABLED_REASON: Record<string, string> = {
  IN_FLIGHT: 'Sending…',
  NO_RECIPIENT: 'No email',
};

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]';

/** One "Send to N garages" outcome, merged over the chunks. `unsent` = ids no request covered (a chunk failed). */
interface MergedResult extends TermsNoticeSendSelectedResult {
  unsent: number;
  error: string | null;
}

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Spec 074 v2 (#1145) — "Send terms…": pick any eligible garages (search, Signed / Not signed filter, select
 * all over the shown rows that can be ticked), confirm with a typed SEND, send in chunks of 100 and show
 * what was sent and what was skipped. A Not-signed garage becomes Signed once its email is delivered; a
 * Signed garage gets the email only. Rows with a notice in flight ("Sending…") or no email can't be ticked.
 */
export default function TermsNoticeSendDialog({ onClose, onSent }: TermsNoticeSendDialogProps) {
  const auth = useAuth();
  const ids = useId();
  const [step, setStep] = useState<Step>('pick');
  const [candidates, setCandidates] = useState<TermsNoticeCandidate[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<MergedResult | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const stepRef = useRef(step);
  stepRef.current = step;
  const handleEscape = useCallback(() => {
    if (stepRef.current === 'sending') return;
    if (stepRef.current === 'confirm') setStep('pick');
    else onClose();
  }, [onClose]);
  useDialogFocus(closeRef, handleEscape);

  const load = useCallback(() => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    api.getTermsNoticeCandidates(auth.token)
      .then((data) => {
        setCandidates(data.candidates);
        // Keep only ticks that can still be sent.
        const selectable = new Set(data.candidates.filter((c) => c.selectable).map((c) => c.garageId));
        setSelected((prev) => new Set([...prev].filter((id) => selectable.has(id))));
      })
      .catch((err) => setError(termsNoticeErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  // Back from the typed confirmation (or on to the result): the main dialog remounts, so put focus back in it.
  useEffect(() => {
    if (step === 'pick' || step === 'result') closeRef.current?.focus();
  }, [step]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (candidates ?? []).filter((c) => {
      if (filter === 'SIGNED' && !isSignedStatus(c.termsStatus)) return false;
      if (filter === 'NOT_SIGNED' && isSignedStatus(c.termsStatus)) return false;
      if (!q) return true;
      return (c.name ?? '').toLowerCase().includes(q)
        || c.recipients.some((r) => r.email.toLowerCase().includes(q));
    });
  }, [candidates, search, filter]);

  const shownSelectable = shown.filter((c) => c.selectable);
  const shownSelectedCount = shownSelectable.filter((c) => selected.has(c.garageId)).length;
  const allShownSelected = shownSelectable.length > 0 && shownSelectedCount === shownSelectable.length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = shownSelectedCount > 0 && !allShownSelected;
    }
  }, [shownSelectedCount, allShownSelected]);

  const picked = useMemo(
    () => (candidates ?? []).filter((c) => c.selectable && selected.has(c.garageId)),
    [candidates, selected],
  );
  const signedCount = picked.filter((c) => isSignedStatus(c.termsStatus)).length;
  const notSignedCount = picked.length - signedCount;

  const toggle = (garageId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(garageId)) next.delete(garageId); else next.add(garageId);
      return next;
    });
  };

  const toggleAllShown = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allShownSelected) shownSelectable.forEach((c) => next.delete(c.garageId));
      else shownSelectable.forEach((c) => next.add(c.garageId));
      return next;
    });
  };

  const handleSend = async () => {
    if (!auth.token || picked.length === 0) return;
    const chunks = chunk(picked.map((c) => c.garageId), TERMS_NOTICE_SEND_CHUNK);
    const merged: MergedResult = {
      sent: 0, recipients: 0, failedRecipients: 0, noticeIds: [], skipped: [], unsent: 0, error: null,
    };
    let succeeded = 0;
    setStep('sending');
    setError(null);
    setProgress({ done: 0, total: picked.length });
    for (let i = 0; i < chunks.length; i++) {
      try {
        const r = await api.sendTermsNoticesTo(chunks[i], auth.token);
        merged.sent += r.sent;
        merged.recipients += r.recipients;
        merged.failedRecipients += r.failedRecipients;
        merged.noticeIds.push(...r.noticeIds);
        merged.skipped.push(...r.skipped);
        succeeded++;
        setProgress({ done: Math.min(picked.length, (i + 1) * TERMS_NOTICE_SEND_CHUNK), total: picked.length });
      } catch (err) {
        merged.error = termsNoticeErrorMessage(err);
        merged.unsent = chunks.slice(i).reduce((n, c) => n + c.length, 0);
        break;
      }
    }
    setProgress(null);
    if (succeeded === 0) {
      // Nothing went out (switch off, permission, network): stay on the picker with the reason.
      setError(merged.error);
      setStep('pick');
      return;
    }
    setResult(merged);
    setSelected(new Set());
    setStep('result');
    onSent();
  };

  if (step === 'confirm') {
    const n = picked.length;
    return (
      <TypedConfirmDialog
        title="Send terms"
        message={`Send the current partner terms to ${plural(n, 'garage')}. `
          + `${plural(notSignedCount, 'not-signed garage')} ${notSignedCount === 1 ? 'becomes' : 'become'} Signed `
          + `once the email is delivered; ${plural(signedCount, 'signed garage')} `
          + `${signedCount === 1 ? 'gets' : 'get'} the email only.`}
        confirmPhrase={SEND_CONFIRM_PHRASE}
        confirmLabel={`Send to ${plural(n, 'garage')}`}
        onConfirm={() => void handleSend()}
        onCancel={() => setStep('pick')}
      />
    );
  }

  // Keep Tab inside the dialog (it is modal).
  const trapTab = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const titleId = `${ids}-title`;
  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div
        ref={dialogRef}
        className="admin-modal admin-modal-lg terms-send-dialog"
        data-testid="terms-notice-send-dialog"
        onKeyDown={trapTab}
      >
        <div className="admin-modal-header">
          <h3 id={titleId}>{step === 'result' ? 'Terms sent' : 'Send terms'}</h3>
          <button
            ref={closeRef}
            type="button"
            className="admin-modal-close"
            onClick={onClose}
            disabled={step === 'sending'}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="admin-modal-body">
          {step === 'result' && result ? (
            <ResultView result={result} />
          ) : step === 'sending' ? (
            <p role="status" className="signed-agreements-muted" data-testid="terms-notice-sending">
              Sending to {plural(picked.length, 'garage')}…
              {progress && progress.total > TERMS_NOTICE_SEND_CHUNK && ` ${progress.done} of ${progress.total} done.`}
            </p>
          ) : loading && !candidates ? (
            <p className="signed-agreements-muted">Loading garages...</p>
          ) : (
            <>
              {error && (
                <div className="admin-info-banner danger signed-agreements-error" role="alert">
                  <span>{error}</span>
                  {!candidates && (
                    <button type="button" className="admin-btn admin-btn-outline signed-agreements-btn" onClick={load}>
                      Retry
                    </button>
                  )}
                </div>
              )}
              {candidates && (
                <PickView
                  idPrefix={ids}
                  shown={shown}
                  total={candidates.length}
                  search={search}
                  onSearch={setSearch}
                  filter={filter}
                  onFilter={setFilter}
                  selected={selected}
                  onToggle={toggle}
                  selectAllRef={selectAllRef}
                  allShownSelected={allShownSelected}
                  canSelectAll={shownSelectable.length > 0}
                  onToggleAll={toggleAllShown}
                />
              )}
            </>
          )}
        </div>

        <div className="admin-modal-footer terms-send-footer">
          {step === 'result' ? (
            <button type="button" className="admin-btn admin-btn-primary" onClick={onClose}>Done</button>
          ) : (
            <>
              <p className="terms-send-count" role="status" aria-live="polite" aria-atomic="true"
                data-testid="terms-notice-selected-count">
                {plural(picked.length, 'selected', 'selected')} · {signedCount} signed, {notSignedCount} not signed
              </p>
              <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={step === 'sending'}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                disabled={step === 'sending' || loading || picked.length === 0}
                aria-busy={step === 'sending' || undefined}
                onClick={() => { setError(null); setStep('confirm'); }}
              >
                {picked.length > 0 ? `Send to ${plural(picked.length, 'garage')}…` : 'Select garages'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PickView({
  idPrefix, shown, total, search, onSearch, filter, onFilter, selected, onToggle, selectAllRef,
  allShownSelected, canSelectAll, onToggleAll,
}: {
  idPrefix: string;
  shown: TermsNoticeCandidate[];
  total: number;
  search: string;
  onSearch: (v: string) => void;
  filter: Filter;
  onFilter: (v: Filter) => void;
  selected: Set<string>;
  onToggle: (garageId: string) => void;
  selectAllRef: RefObject<HTMLInputElement>;
  allShownSelected: boolean;
  canSelectAll: boolean;
  onToggleAll: () => void;
}) {
  const searchId = `${idPrefix}-search`;
  const filterId = `${idPrefix}-filter`;
  return (
    <>
      <p className="signed-agreements-muted" style={{ marginTop: 0 }}>
        Each garage gets the current partner terms as PDFs at its email and its owner&apos;s. A Not-signed garage
        becomes Signed once its email is delivered; a Signed garage gets the email only.
      </p>

      <div className="terms-send-toolbar">
        <div className="terms-send-field terms-send-field-grow">
          <label htmlFor={searchId}>Search</label>
          <input
            id={searchId}
            type="search"
            className="admin-search-input"
            placeholder="Garage name or email"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
        <div className="terms-send-field">
          <label htmlFor={filterId}>Terms</label>
          <select
            id={filterId}
            className="admin-select"
            data-testid="terms-send-filter"
            value={filter}
            onChange={(e) => onFilter(e.target.value as Filter)}
          >
            {FILTER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <p className="signed-agreements-sr-only" role="status" aria-live="polite">
        {`${plural(shown.length, 'garage')} shown of ${total}`}
      </p>

      {shown.length === 0 ? (
        <div className="admin-empty-state" data-testid="terms-notice-empty"><p>No garages match.</p></div>
      ) : (
        <div className="terms-notice-list" tabIndex={0} role="region" aria-label="Garages to send the terms to">
          <table className="admin-table terms-send-table">
            <thead>
              <tr>
                <th scope="col" className="terms-send-check">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    className="terms-send-checkbox"
                    checked={allShownSelected}
                    disabled={!canSelectAll}
                    onChange={onToggleAll}
                    aria-label="Select all shown garages that can be emailed"
                    data-testid="terms-send-select-all"
                  />
                </th>
                <th scope="col">Garage</th>
                <th scope="col">Terms</th>
                <th scope="col">Recipients</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => {
                const checkboxId = `${idPrefix}-g-${c.garageId}`;
                const isChecked = c.selectable && selected.has(c.garageId);
                const disabledReason = c.selectable ? null : (DISABLED_REASON[c.reason ?? ''] ?? 'Unavailable');
                return (
                  <tr
                    key={c.garageId}
                    data-testid="terms-notice-candidate"
                    className={`${isChecked ? 'terms-send-row-selected' : ''} ${c.selectable ? '' : 'terms-send-row-disabled'}`.trim()}
                  >
                    <td className="terms-send-check">
                      <input
                        id={checkboxId}
                        type="checkbox"
                        className="terms-send-checkbox"
                        checked={isChecked}
                        disabled={!c.selectable}
                        onChange={() => onToggle(c.garageId)}
                        aria-describedby={disabledReason ? `${checkboxId}-why` : undefined}
                      />
                    </td>
                    <td>
                      <label htmlFor={checkboxId} className="terms-send-name" dir="auto">{c.name}</label>
                      {c.blocked && <span className="admin-badge rejected terms-send-chip">Blocked</span>}
                      {disabledReason && (
                        <div id={`${checkboxId}-why`} className="terms-send-why" data-testid="terms-notice-disabled-reason">
                          {disabledReason}
                        </div>
                      )}
                    </td>
                    <td><TermsStatusBadge status={c.termsStatus} testId="terms-send-badge" /></td>
                    <td>
                      {c.recipients.length === 0 ? (
                        <span className="signed-agreements-muted-inline">—</span>
                      ) : (
                        <ul className="terms-notice-emails">
                          {c.recipients.map((r) => (
                            <li key={`${r.email}-${r.kind}`}>
                              <span className="terms-notice-email">{r.email}</span>
                              <span className="signed-agreements-muted-inline"> · {RECIPIENT_KIND_LABEL[r.kind] ?? r.kind}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function SkippedList({ skipped }: { skipped: TermsNoticeSkipped[] }) {
  return (
    <div style={{ marginTop: '1.25rem' }} data-testid="terms-notice-skipped">
      <h4 className="terms-notice-heading">Not sent ({skipped.length})</h4>
      <div className="terms-notice-list terms-notice-list-sm" tabIndex={0} role="region" aria-label="Garages not emailed">
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Garage</th>
              <th scope="col">Reason</th>
            </tr>
          </thead>
          <tbody>
            {skipped.map((s) => (
              <tr key={s.garageId}>
                <td dir="auto">{s.name ?? <span className="signed-agreements-muted-inline">Unknown garage</span>}</td>
                <td>{skipReasonLabel(s.reason)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResultView({ result }: { result: MergedResult }) {
  return (
    <div data-testid="terms-notice-result">
      <div className="admin-info-banner success" role="status">
        Sent to {plural(result.sent, 'garage')} ({plural(result.recipients, 'email')}).
        Not-signed garages become Signed once their email is delivered.
      </div>
      {result.failedRecipients > 0 && (
        <div className="admin-info-banner warning" role="alert">
          {plural(result.failedRecipients, 'email')} could not be queued. Send again to retry them.
        </div>
      )}
      {result.error && (
        <div className="admin-info-banner danger" role="alert" data-testid="terms-notice-partial-error">
          {plural(result.unsent, 'garage')} not sent: {result.error} Send again for the rest.
        </div>
      )}
      {result.skipped.length > 0 && <SkippedList skipped={result.skipped} />}
    </div>
  );
}
